import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminClientsGetAdminClientsIdGuidQueryKey,
  getAdminClientsGetAdminClientsQueryKey,
  useAdminClientsGetAdminClientsIdGuid,
  useAdminClientsPutAdminClientsIdGuid,
} from '@/lib/api/admin-clients/admin-clients';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  name: z.string().min(1, 'Name is required.').max(200),
  rateLimitPerMin: z.coerce.number().int().positive().max(10_000),
  allowedIpCidrs: z.string().max(400).optional().or(z.literal('')),
  adyenBalancePlatform: z.string().max(256).optional().or(z.literal('')),
  adyenScopePlatform: z.string().max(256).optional().or(z.literal('')),
  adyenApiKey: z.string().max(2048).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

interface Props {
  clientId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function EditClientDialog({ clientId, onOpenChange }: Props) {
  const open = clientId !== null;
  const queryClient = useQueryClient();

  // Fetch fresh detail when the dialog opens so we pre-fill from the
  // authoritative server state (not a possibly-stale list cache).
  const detailQuery = useAdminClientsGetAdminClientsIdGuid(clientId ?? '', {
    query: { enabled: open, staleTime: 0 },
  });

  const mutation = useAdminClientsPutAdminClientsIdGuid({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getAdminClientsGetAdminClientsQueryKey() }),
          clientId
            ? queryClient.invalidateQueries({
                queryKey: getAdminClientsGetAdminClientsIdGuidQueryKey(clientId),
              })
            : Promise.resolve(),
        ]);
        toast.success('Client updated.');
        onOpenChange(false);
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: '',
      rateLimitPerMin: 120,
      allowedIpCidrs: '',
      adyenBalancePlatform: '',
      adyenScopePlatform: '',
      adyenApiKey: '',
    },
  });

  // Hydrate form once detail arrives.
  useEffect(() => {
    if (!detailQuery.data) return;
    const d = detailQuery.data;
    reset({
      name: d.name,
      rateLimitPerMin: Number(d.rateLimitPerMin),
      allowedIpCidrs: (d.allowedIpCidrs ?? []).join(', '),
      adyenBalancePlatform: d.adyenBalancePlatform ?? '',
      adyenScopePlatform: d.adyenScopePlatform ?? '',
      adyenApiKey: '',
    });
  }, [detailQuery.data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!clientId) return;
    const cidrs = values.allowedIpCidrs
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      await mutation.mutateAsync({
        id: clientId,
        data: {
          name: values.name,
          rateLimitPerMin: values.rateLimitPerMin,
          allowedIpCidrs: cidrs && cidrs.length > 0 ? cidrs : null,
          adyenBalancePlatform: values.adyenBalancePlatform || null,
          adyenScopePlatform: values.adyenScopePlatform || null,
          // Empty = keep existing key on the server.
          adyenApiKey: values.adyenApiKey || null,
        },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 400 ? 'Validation failed — check CIDRs.' : 'Could not update client.');
    }
  });

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const detail = detailQuery.data;
  const existingKeyMasked = detail?.adyenApiKeyMasked;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit client</DialogTitle>
          <DialogDescription>
            {detail ? `Editing ${detail.name} (${detail.clientId}).` : 'Loading client…'}
          </DialogDescription>
        </DialogHeader>

        {detailQuery.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !detail ? (
          <div className="py-12 text-center text-sm text-destructive">Client not found.</div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" {...register('name')} aria-invalid={!!errors.name} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-rateLimitPerMin">Rate limit (requests / minute)</Label>
              <Input id="edit-rateLimitPerMin" type="number" min={1} {...register('rateLimitPerMin')} />
              {errors.rateLimitPerMin && (
                <p className="text-xs text-destructive">{errors.rateLimitPerMin.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-allowedIpCidrs">Allowed IP CIDRs (comma-separated)</Label>
              <Input
                id="edit-allowedIpCidrs"
                placeholder="10.0.0.0/8, 192.168.1.0/24"
                {...register('allowedIpCidrs')}
              />
            </div>

            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
              <div className="text-xs font-medium text-muted-foreground">Adyen credentials</div>
              <div className="space-y-2">
                <Label htmlFor="edit-adyenBalancePlatform">Balance Platform</Label>
                <Input
                  id="edit-adyenBalancePlatform"
                  placeholder="AQE..."
                  autoComplete="off"
                  {...register('adyenBalancePlatform')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-adyenScopePlatform">Scope Platform</Label>
                <Input
                  id="edit-adyenScopePlatform"
                  placeholder="AQE..."
                  autoComplete="off"
                  {...register('adyenScopePlatform')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-adyenApiKey">API Key</Label>
                <Input
                  id="edit-adyenApiKey"
                  type="password"
                  placeholder={existingKeyMasked ? `Current: ${existingKeyMasked}` : 'Not configured'}
                  autoComplete="off"
                  {...register('adyenApiKey')}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to keep the existing key. Enter a new value to rotate.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || mutation.isPending || !isDirty}>
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
